<template>
  <div class="mfa-page d-flex align-items-center justify-content-center">
    <div class="mfa-card">
      <div class="text-center mb-4">
        <img src="images/logo-white-shadow.svg" alt="Logo" class="mfa-logo mb-2">
        <h1 class="mfa-title">
          Vérification de connexion
        </h1>
      </div>

      <p class="mfa-description text-center">
        Entrez le code envoyé à {{ challenge ? challenge.masked_email : 'votre adresse e-mail' }}.
      </p>

      <form @submit.prevent="handleVerify">
        <b-form-group label="Code de vérification" label-for="mfa-code" class="mfa-label">
          <b-form-input id="mfa-code" v-model="code" type="password" inputmode="numeric"
                        autocomplete="one-time-code" pattern="[0-9]*" minlength="4" maxlength="10"
                        required autofocus :disabled="isSubmitting" @input="normalizeCode" />
        </b-form-group>

        <div v-if="errorMessage" class="mfa-error text-center" role="alert">
          {{ errorMessage }}
        </div>

        <b-button type="submit" variant="primary" block class="mt-4" :disabled="isSubmitting || code.length < 4">
          {{ isSubmitting ? 'Vérification...' : 'Vérifier le code' }}
        </b-button>

        <b-button type="button" variant="link" block class="mt-2" :disabled="isResending || secondsUntilResend > 0"
                  :aria-label="resendLabel" @click="handleResend">
          {{ resendLabel }}
        </b-button>

        <b-button type="button" variant="link" block @click="redirectToLogin">
          Retour à la connexion
        </b-button>
      </form>

      <p class="mfa-countdown text-center" aria-live="polite">
        Code valable jusqu'à {{ challenge ? formatDate(challenge.expires_at) : '' }}.
      </p>
    </div>
  </div>
</template>

<script>
import axios from '@/libs/axiosIns'
import store from '@/store'
import {
  clearPendingMfaChallenge,
  getPendingMfaChallenge,
  isMfaChallengeExpired,
  setPendingMfaChallenge,
} from '@/utils/mfaChallenge'
import {
  BButton,
  BFormGroup,
  BFormInput,
} from 'bootstrap-vue'

export default {
  name: 'MfaVerification',
  components: {
    BButton,
    BFormGroup,
    BFormInput,
  },
  data() {
    return {
      challenge: null,
      code: '',
      errorMessage: null,
      isResending: false,
      isSubmitting: false,
      now: Date.now(),
      countdownTimer: null,
    }
  },
  computed: {
    secondsUntilResend() {
      if (!this.challenge || !this.challenge.resend_available_at) { return 0 }

      const resendAvailableAt = Date.parse(this.challenge.resend_available_at)
      if (Number.isNaN(resendAvailableAt)) { return 0 }

      return Math.max(0, Math.ceil((resendAvailableAt - this.now) / 1000))
    },
    resendLabel() {
      if (this.isResending) { return 'Envoi en cours...' }
      if (this.secondsUntilResend > 0) {
        return `Renvoyer le code dans ${this.formatCountdown(this.secondsUntilResend)}`
      }
      return 'Renvoyer le code'
    },
  },
  mounted() {
    this.challenge = getPendingMfaChallenge()

    if (!this.challenge || isMfaChallengeExpired(this.challenge)) {
      this.redirectToLogin()
      return
    }

    this.countdownTimer = window.setInterval(() => {
      this.now = Date.now()
    }, 1000)
  },
  beforeDestroy() {
    if (this.countdownTimer) {
      window.clearInterval(this.countdownTimer)
    }
  },
  methods: {
    formatCountdown(totalSeconds) {
      const minutes = Math.floor(totalSeconds / 60)
      const seconds = String(totalSeconds % 60).padStart(2, '0')
      return `${minutes}:${seconds}`
    },
    formatDate(value) {
      return new Intl.DateTimeFormat('fr-FR', {
        dateStyle: 'short',
        timeStyle: 'short',
      }).format(new Date(value))
    },
    normalizeCode(value) {
      this.code = value.replace(/\D/g, '').slice(0, 10)
    },
    isCurrentChallengeExpired() {
      return !this.challenge || isMfaChallengeExpired(this.challenge)
    },
    redirectToLogin() {
      clearPendingMfaChallenge()
      this.$router.replace({ name: 'login' })
    },
    async handleVerify() {
      if (this.isCurrentChallengeExpired()) {
        this.redirectToLogin()
        return
      }

      this.errorMessage = null
      this.isSubmitting = true

      try {
        const response = await axios.post('/Auth/VerifyMfa', {
          challenge_id: this.challenge.challenge_id,
          code: this.code,
        })

        if (response.data.status === 'authenticated') {
          await store.dispatch('auth/establishSession')
          await this.$router.replace({ name: 'planning' })
          return
        }

        if (response.data.status === 'mfa_recovery_required') {
          clearPendingMfaChallenge()
          await this.$router.replace({ name: 'mfa-recovery' })
          return
        }

        this.errorMessage = 'Impossible de vérifier le code. Veuillez réessayer.'
      } catch (error) {
        if (this.isCurrentChallengeExpired()) {
          this.redirectToLogin()
          return
        }

        this.errorMessage = 'Code invalide ou expiré. Veuillez réessayer.'
      } finally {
        this.isSubmitting = false
      }
    },
    async handleResend() {
      if (this.isCurrentChallengeExpired()) {
        this.redirectToLogin()
        return
      }

      if (this.secondsUntilResend > 0) { return }

      this.errorMessage = null
      this.isResending = true

      try {
        const response = await axios.post('/Auth/ResendMfa', {
          challenge_id: this.challenge.challenge_id,
        })

        if (response.data.status === 'mfa_required' && response.data.challenge) {
          this.challenge = response.data.challenge
          this.code = ''
          this.now = Date.now()
          setPendingMfaChallenge(this.challenge)
          return
        }

        if (response.data.status === 'mfa_recovery_required') {
          clearPendingMfaChallenge()
          await this.$router.replace({ name: 'mfa-recovery' })
          return
        }

        this.redirectToLogin()
      } catch (error) {
        if (this.isCurrentChallengeExpired()) {
          this.redirectToLogin()
          return
        }

        if (error.response && error.response.status === 400) {
          this.redirectToLogin()
          return
        }

        this.errorMessage = 'Impossible de renvoyer le code. Veuillez réessayer.'
      } finally {
        this.isResending = false
      }
    },
  },
}
</script>

<style scoped>
.mfa-page {
  min-height: 100vh;
  padding: 1rem;
  color: white;
  background: #202329 url('/images/refuge.jpg') center / cover;
  position: relative;
}

.mfa-page::before {
  content: '';
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.62);
}

.mfa-card {
  width: min(100%, 400px);
  padding: 2rem 3rem;
  border-radius: 10px;
  background: rgba(46, 46, 46, 0.9);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.3);
  position: relative;
}

.mfa-logo {
  max-width: 180px;
}

.mfa-title {
  font-size: 1.5rem;
  font-weight: bold;
}

.mfa-description,
.mfa-countdown {
  color: #e5e5e5;
}

.mfa-label label {
  color: white !important;
}

.mfa-error {
  color: #ffb3b3;
  font-size: 0.9rem;
  font-weight: 800;
}

.mfa-countdown {
  margin: 1rem 0 0;
  font-size: 0.8rem;
}

@media (max-width: 480px) {
  .mfa-card {
    padding: 1.5rem;
  }
}
</style>
